import styled, { css } from 'styled-components'

export const DrawingToolsContainer = styled.div`
  position: absolute;
  bottom: 24px;
  right: 16px;
  z-index: 5;

  @media (max-width: 768px) {
    bottom: 0px;
    right: 0px;
  }
`

export const DrawingToolsFAB = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{
  $active?: boolean
  $position?: {
    bottom?: string
    left?: string
    right?: string
  }
  $zIndex?: number
}>`
  position: relative;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 173, 230, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: ${({ $zIndex }) => $zIndex || 5};
  transform: ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 173, 230, 0.4);
  }

  &:active {
    transform: scale(0.95)
      ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};
  }

  .icon,
  svg {
    width: 24px;
    height: 24px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  @media (max-width: 768px) {
    bottom: 16px;
    right: 8px;
  }
`

export const DrawingToolsPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 56px;
  right: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 8px;
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  transform-origin: bottom right;
  animation: ${({ $active }) =>
    $active ? 'popIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'};
  z-index: 5;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 768px) {
    bottom: 56px;
    right: 0;
    min-width: 160px;
    max-width: calc(100vw - 32px);
  }
`

export const DrawingToolButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean; $variant?: 'default' | 'delete'; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  color: ${({ $variant, theme }) =>
    $variant === 'delete' ? '#ef4444' : theme.colors.text.primary};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) =>
      $variant === 'delete'
        ? 'rgba(239, 68, 68, 0.1)'
        : theme.colors.background.secondary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    `}

  .icon,
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: ${({ theme }) => theme.typography.fontSize.micro};

    .icon,
    svg {
      width: 14px;
      height: 14px;
    }
  }
`

export const DrawToolsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 4px 8px;
`
